import React, { useEffect, useMemo, useState } from "react"
import {
  ButtonBase,
  ClickAwayListener,
  Paper,
  Popper,
  Tab,
  Tabs,
  TextField,
  Theme,
  Typography,
  makeStyles,
} from "@material-ui/core"
import { useTranslation } from "react-i18next"
import { Emoji, emojiCategories, searchEmojis } from "../utils/emojis"

type Props = {
  anchorEl: HTMLElement | null
  onSelect: (emoji: string) => void
  onClose: () => void
}

const ANCHOR_DRIFT_TOLERANCE = 4

// Keeps focus, and so the caret, in the field being typed into.
const keepFocus = (event: React.MouseEvent) => event.preventDefault()

const EmojiPicker = ({ anchorEl, onSelect, onClose }: Props) => {
  const classes = useStyles()
  const { t } = useTranslation("chat")

  const open = Boolean(anchorEl)
  const [search, setSearch] = useState("")
  const [categoryIndex, setCategoryIndex] = useState(0)

  useEffect(() => {
    if (open) {
      setSearch("")
      setCategoryIndex(0)
    }
  }, [open])

  // An anchor inside the scrolling message list drifts away from what it belongs to. The
  // composer's anchor doesn't move with the list, so its picker survives this.
  useEffect(() => {
    if (!anchorEl) return

    const anchoredAt = anchorEl.getBoundingClientRect().top

    const closeIfAnchorMoved = () => {
      if (Math.abs(anchorEl.getBoundingClientRect().top - anchoredAt) > ANCHOR_DRIFT_TOLERANCE) onClose()
    }

    // Scroll events don't bubble, so this listens on the way down instead.
    document.addEventListener("scroll", closeIfAnchorMoved, { capture: true, passive: true })

    return () => document.removeEventListener("scroll", closeIfAnchorMoved, { capture: true })
  }, [anchorEl, onClose])

  const results: Emoji[] = useMemo(
    () => (search.trim() ? searchEmojis(search) : emojiCategories[categoryIndex].emojis),
    [search, categoryIndex]
  )

  const handleClickAway = (event: React.MouseEvent<Document>) => {
    // The trigger button does its own toggling — closing here as well would
    // reopen the picker on the very click meant to dismiss it.
    if (anchorEl?.contains(event.target as Node)) return

    onClose()
  }

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="top-end"
      className={classes.popper}
      modifiers={{ preventOverflow: { boundariesElement: "viewport" } }}
    >
      {/* On mousedown, so the button that was pressed can reopen the picker straight
          after — which is how it moves between two messages' reaction buttons. */}
      <ClickAwayListener mouseEvent="onMouseDown" onClickAway={handleClickAway}>
        <Paper
          elevation={8}
          className={classes.root}
          onKeyDown={(event) => {
            if (event.key === "Escape") onClose()
          }}
        >
          <TextField
            className={classes.search}
            placeholder={t("emojiPicker.searchPlaceholder")}
            variant="outlined"
            size="small"
            fullWidth
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />

          {!search.trim() && (
            <Tabs
              value={categoryIndex}
              onChange={(_, value) => setCategoryIndex(value)}
              variant="scrollable"
              scrollButtons="off"
              indicatorColor="primary"
              classes={{ root: classes.tabs }}
            >
              {emojiCategories.map((category) => (
                <Tab
                  key={category.id}
                  className={classes.tab}
                  label={category.icon}
                  title={t(category.labelKey)}
                  onMouseDown={keepFocus}
                />
              ))}
            </Tabs>
          )}

          <div className={classes.grid}>
            {results.map((emoji) => (
              <ButtonBase
                key={emoji.char}
                className={classes.emoji}
                title={emoji.keywords}
                onMouseDown={keepFocus}
                onClick={() => onSelect(emoji.char)}
              >
                {emoji.char}
              </ButtonBase>
            ))}
          </div>

          {results.length === 0 && (
            <Typography className={classes.empty} color="textSecondary">
              {t("emojiPicker.emptyMessage")}
            </Typography>
          )}
        </Paper>
      </ClickAwayListener>
    </Popper>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  popper: {
    zIndex: theme.zIndex.modal,
  },

  root: {
    width: 320,
    // The popper is as wide as the smallest phones, so it has to be allowed to shrink.
    maxWidth: "calc(100vw - 16px)",
    padding: theme.spacing(1),
  },

  search: {
    marginBottom: theme.spacing(1),
  },

  tabs: {
    minHeight: 36,
    marginBottom: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },

  tab: {
    minWidth: 36,
    minHeight: 36,
    fontSize: 18,
  },

  grid: {
    height: 220,
    overflowY: "auto",
    display: "grid",
    gridTemplateColumns: "repeat(8, 1fr)",
  },

  emoji: {
    height: 36,
    borderRadius: 6,
    fontSize: 22,
    lineHeight: 1,

    "&:hover": {
      background: theme.palette.action.hover,
    },
  },

  empty: {
    padding: theme.spacing(2),
    textAlign: "center",
  },
}))

export default EmojiPicker
